import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  ForgeButton,
  ForgeDialog,
  ForgeScaffold,
  ForgeStack,
  ForgeToolbar
} from '@tylertech/forge-react';
import { removeMfaFactor } from '../../api/mfa';
import { MFA_FACTORS_QUERY_KEY } from '../../hooks/useMfaFactors';

export default function DisableSmsDialog({ open, factorId, onClose, onComplete }) {
  const queryClient = useQueryClient();
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleClose = () => {
    if (isSubmitting) return;
    setError('');
    onClose();
  };

  const handleConfirm = async () => {
    if (!factorId || isSubmitting) return;

    setError('');
    setIsSubmitting(true);
    try {
      await removeMfaFactor(factorId);
      await queryClient.invalidateQueries({ queryKey: MFA_FACTORS_QUERY_KEY });
      if (typeof onComplete === 'function') {
        onComplete();
      }
      setError('');
      onClose();
    } catch (err) {
      const message = err?.response?.data?.message || 'Unable to disable SMS authentication. Please try again.';
      setError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ForgeDialog
      className="dialog--small"
      open={open}
      on-forge-dialog-close={handleClose}
      label="Disable SMS Authentication"
      description="Confirm that you want to disable SMS authentication.">
      <ForgeScaffold>
        <ForgeToolbar slot="header" className="dialog-header">
          <h2 slot="start" className="forge-typography--subheading6">
            Disable SMS Authentication
          </h2>
        </ForgeToolbar>
        <div slot="body" className="dialog-body">
          <ForgeStack gap="16">
            {error ? <p className="dialog-status dialog-status--error">{error}</p> : null}
            <p className="forge-typography--body1">
              Are you sure you want to disable SMS text message authentication?
              You will no longer receive verification codes via text message when signing in.
            </p>
          </ForgeStack>
        </div>
        <ForgeToolbar slot="footer" inverted>
          <ForgeStack slot="end" inline>
            <ForgeButton variant="outlined" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </ForgeButton>
            <ForgeButton variant="filled" theme="error" onClick={handleConfirm} disabled={isSubmitting}>
              {isSubmitting ? 'Disabling...' : 'Disable'}
            </ForgeButton>
          </ForgeStack>
        </ForgeToolbar>
      </ForgeScaffold>
    </ForgeDialog>
  );
}
